import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { enqueueSnackbar } from "notistack";

import { POST_LOSS_ROUTES } from "@/shared/constants/paths";
import { TOAST } from "@/shared/constants/toastMessages";
import { supabase } from "@/shared/lib/supabase";
import { getGoogleOAuthContext, clearGoogleOAuthContext } from "@/shared/utils/googleOAuth";
import { saveLastAuthMethod } from "@/shared/utils/lastAuthMethod";
import { stripPhone } from "@/shared/utils/formatters";
import { getHelpTopicsFromAnswers } from "@/shared/constants/helpTopics";
import {
  acceptInvitation,
  acceptInvitationForExistingUser,
} from "@/shared/utils/invitationAcceptance";
import { promoteAdminIfElayne, setAcquisitionSource } from "@/shared/utils/onboarding";
import { EventType, trackEvent } from "@/shared/utils/core";
import { toErrorType, trackUserSignedUp } from "@/shared/utils/funnelAnalytics";

import type { FlowAnswers } from "@/shared/constants/flowTypes";

function splitName(fullName: string): { firstName: string; lastName: string } {
  const parts = fullName.trim().split(/\s+/);
  return { firstName: parts[0] || "", lastName: parts.slice(1).join(" ") || "" };
}

async function handleExistingUserOAuth(authToken: string, email: string): Promise<boolean> {
  return acceptInvitationForExistingUser(authToken, email);
}

async function handleNewUserOAuth(
  authToken: string,
  user: { id: string; email: string },
  answers: Record<string, unknown>,
): Promise<boolean> {
  const fullName = (answers["invitee-full-name"] as string) || "";
  const phoneNumber = (answers["invitee-phone"] as string) || "";
  const relationship = (answers["invitee-relationship"] as string) || "";
  const helpTopics = getHelpTopicsFromAnswers(answers as FlowAnswers);
  const { firstName, lastName } = splitName(fullName);

  const { data: userRecord, error } = await supabase
    .from("users")
    .insert({
      auth_id: user.id,
      email: user.email.toLowerCase().trim(),
      first_name: firstName,
      last_name: lastName,
      full_name: fullName,
      phone_number: stripPhone(phoneNumber),
      completed_onboarding: false,
      completed_authorization: false,
      contact_by_email: true,
      contact_by_phone: false,
      help_topics: helpTopics,
    })
    .select()
    .single();

  if (error) {
    trackEvent(EventType.SIGNUP_FAILED, {
      flow: "family_invite",
      signup_method: "google",
      error_type: toErrorType(error),
    });
    console.error("Failed to create user record:", error);
    return false;
  }

  trackUserSignedUp("family_invite", "google");

  promoteAdminIfElayne(userRecord.user_id, user.email).catch((err) =>
    console.warn("Admin promotion failed (non-blocking):", err),
  );

  setAcquisitionSource(user.email.toLowerCase().trim(), "nok").catch((err) =>
    console.warn("Acquisition source tracking failed (non-blocking):", err),
  );

  return acceptInvitation(authToken, user.email, relationship);
}

export function useOAuthInviteReturn(): boolean {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const handled = useRef(false);

  const [processing, setProcessing] = useState(() => {
    const ctx = getGoogleOAuthContext();
    return ctx?.type === "invite-existing" || ctx?.type === "invite-new";
  });

  useEffect(() => {
    if (!processing || handled.current) return;
    handled.current = true;

    const run = async () => {
      const ctx = getGoogleOAuthContext();
      if (!ctx || (ctx.type !== "invite-existing" && ctx.type !== "invite-new") || !ctx.token) {
        clearGoogleOAuthContext();
        setProcessing(false);
        return;
      }

      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user?.email) {
        clearGoogleOAuthContext();
        setProcessing(false);
        return;
      }

      const accepted =
        ctx.type === "invite-existing"
          ? await handleExistingUserOAuth(ctx.token, user.email)
          : await handleNewUserOAuth(
              ctx.token,
              { id: user.id, email: user.email },
              ctx.answers ?? {},
            );

      clearGoogleOAuthContext();
      saveLastAuthMethod("google");

      if (!accepted) {
        setProcessing(false);
        return;
      }

      const { refetchCriticalQueries } = await import("@/shared/utils/queryRefetchHelper");
      await refetchCriticalQueries(queryClient);
      await queryClient.invalidateQueries({ queryKey: ["userContacts"] });
      await queryClient.invalidateQueries({ queryKey: ["personInvitations"] });
      await queryClient.invalidateQueries({ queryKey: ["personInvitationsAccepted"] });

      enqueueSnackbar(TOAST.ACCOUNT_JOINED, { variant: "success" });
      navigate(POST_LOSS_ROUTES.OVERVIEW, { replace: true });
    };

    run();
  }, [processing, queryClient, navigate]);

  return processing;
}
